import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, AlertCircle, DollarSign, Percent, FileText } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/home/CTASection';
import { rebates } from '../data/mock';

const RebatesPage = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Solar &amp; Energy Rebates — Federal STCs + State Programs"
        description="Federal STCs, VEECs (VIC), ESCs/PDRS (NSW), REPS (SA), EEIS (ACT) and other Australian energy rebates explained — how they apply to your solar, battery, and heat pump install."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 hero-gradient">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="inline-block text-[#B5D43B] font-semibold text-sm tracking-wider uppercase mb-4">
                Rebates & Incentives
              </span>
              <h1 className="heading-1 mb-6">
                Make Energy Upgrades More Affordable
              </h1>
              <p className="body-large">
                Significant government incentives are available to help reduce the 
                upfront cost of solar, batteries, and other energy solutions. We help 
                you navigate and access all eligible rebates.
              </p>
            </div>
          </div>
        </section>

        {/* Disclaimer Banner */}
        <section className="py-6 bg-[#B5D43B]/15 border-y border-[#B5D43B]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-[#3D5A47] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#3D5A47]">
                <strong>Important:</strong> Rebate programs and eligibility criteria change 
                regularly. The information below is a general guide only. We'll provide 
                up-to-date details during your consultation.
              </p>
            </div>
          </div>
        </section>

        {/* Rebates Detail Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {rebates.map((rebate, index) => (
                <div 
                  key={rebate.id}
                  className="bg-white rounded-2xl border border-black/5 p-6 md:p-8 hover:shadow-lg transition-shadow"
                >
                  <div className="w-14 h-14 rounded-2xl bg-[#B5D43B]/15 flex items-center justify-center mb-6">
                    {index === 0 && <DollarSign className="w-7 h-7 text-[#3D5A47]" />}
                    {index === 1 && <Percent className="w-7 h-7 text-[#3D5A47]" />}
                    {index === 2 && <FileText className="w-7 h-7 text-[#3D5A47]" />}
                  </div>
                  <h2 className="heading-3 mb-4">{rebate.title}</h2>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {rebate.description}
                  </p>
                  <div className="pt-4 border-t border-black/5">
                    <p className="text-sm">
                      <span className="font-semibold text-[#3D5A47]">Eligibility: </span>
                      <span className="text-gray-600">{rebate.eligibility}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How We Help Section */}
        <section className="py-20 section-light">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <h2 className="heading-2 mb-6">We Handle the Paperwork</h2>
                <p className="body-large mb-6">
                  Navigating government rebates can be confusing. That's why we manage 
                  the entire process for you.
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#B5D43B] text-[#3D5A47] flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                      1
                    </div>
                    <div>
                      <p className="font-semibold text-[#3D5A47]">Eligibility Assessment</p>
                      <p className="text-gray-600 text-sm">We check which rebates you qualify for</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#B5D43B] text-[#3D5A47] flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                      2
                    </div>
                    <div>
                      <p className="font-semibold text-[#3D5A47]">Application Support</p>
                      <p className="text-gray-600 text-sm">We help prepare and submit applications</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#B5D43B] text-[#3D5A47] flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                      3
                    </div>
                    <div>
                      <p className="font-semibold text-[#3D5A47]">Upfront Discount</p>
                      <p className="text-gray-600 text-sm">Most rebates are applied directly to your quote</p>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="bg-white rounded-2xl border border-black/5 p-8">
                <h3 className="font-semibold text-lg text-[#3D5A47] mb-6">
                  Want to know what you're eligible for?
                </h3>
                <p className="text-gray-600 mb-6">
                  Our team can assess your eligibility for all available rebates and 
                  provide a clear breakdown of potential savings.
                </p>
                <Link to="/contact" className="btn-primary inline-flex gap-2">
                  Check My Eligibility
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </div>
  );
};

export default RebatesPage;
